import React, { useState, useEffect, useMemo } from "react";
import { Prompt } from "react-router";
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";

import CloseIcon from "@material-ui/icons/Close";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";

import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import FilterDropdown from "designV2/components/FilterDropdown";
import SearchField from "designV2/components/SearchField";
import StatusBadge from "designV2/components/StatusBadge";
import { endPoint } from "utils/config";
import { readableError } from "../detailFields";
import "./tripPromotionForm.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const PROMOTION_INPUT = `
  condition: $condition
  type: $type
  for: $for
  canUse: $canUse
  canNotUse: $canNotUse
  from: $from
  to: $to
  percent: $percent
  maximumPromotion: $maximumPromotion
  useLimitCount: $useLimitCount
  promotionCode: $promotionCode
`;

const PROMOTION_VARIABLES = `
  $condition: promotionConditionEnum
  $type: promotionTypeEnum
  $for: forEnum
  $canUse: [ID]
  $canNotUse: [ID]
  $from: Date
  $to: Date
  $percent: Float
  $maximumPromotion: Float
  $useLimitCount: Int
  $promotionCode: String!
`;

const CREATE_PROMOTION = gql`
  mutation createTripPromotionByAdmin(${PROMOTION_VARIABLES}) {
    createTripPromotionByAdmin(data: { ${PROMOTION_INPUT} }) {
      _id
    }
  }
`;

const UPDATE_PROMOTION = gql`
  mutation updateTripPromotionByAdmin($promotionId: ID!, ${PROMOTION_VARIABLES}) {
    updateTripPromotionByAdmin(promotionId: $promotionId, data: { ${PROMOTION_INPUT} }) {
      _id
    }
  }
`;

const SEARCH_USERS = gql`
  query searchUsersForPromotion($fullName: String) {
    getUsersByAdmin(filters: { fullName: $fullName }, pagination: { limit: 10, skip: 0 }) {
      _id
      fullName
      phoneNumber
    }
  }
`;

/**
 * Editing a promotion gives back customer ids only, so the names behind
 * them have to be looked up. getUsersByAdmin filters on a single `_id`
 * and has no id-set filter, so the ids are aliased into one document
 * rather than fetched one at a time — the previous form walked them with
 * an index counter that fired a fresh query per customer and appended to
 * a growing array, which double-counted whenever it re-rendered.
 *
 * Capped because the document grows with the audience; a promotion
 * targeted at more people than this shows the rest as ids.
 */
const AUDIENCE_LOOKUP_LIMIT = 50;

const buildAudienceQuery = (ids) => gql`
  query getPromotionAudience {
    ${ids
      .map(
        (id, index) => `u${index}: getUsersByAdmin(filters: { _id: "${id}" }) {
          _id
          fullName
          phoneNumber
        }`
      )
      .join("\n")}
  }
`;

const AUDIENCE_PLACEHOLDER_QUERY = buildAudienceQuery(["000000000000000000000000"]);

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const TYPE_OPTIONS = [{ value: "PERCENT", label: "Percentage off" }];
const TYPE_LABELS = { PERCENT: "Percentage off", FIXED: "Fixed amount off" };

const CONDITION_OPTIONS = [
  { value: "TIMELY", label: "Time limited" },
  { value: "FIRST_TRIP", label: "First trip only" },
];
const CONDITION_LABELS = CONDITION_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const FOR_OPTIONS = [
  { value: "ALL", label: "Rides and deliveries" },
  { value: "RIDE", label: "Rides only" },
  { value: "DELIVERY", label: "Deliveries only" },
];
const FOR_LABELS = FOR_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const AUDIENCE_OPTIONS = [
  { value: "everyone", label: "Everyone" },
  { value: "everyoneExcept", label: "Everyone except…" },
  { value: "only", label: "Only chosen customers" },
];

const SEARCH_DEBOUNCE_MS = 300;
const DATE_FORMAT = "YYYY-MM-DDTHH:mm";

const toInput = (value) => (value ? moment(value).format(DATE_FORMAT) : "");

/**
 * A chip list of chosen customers plus a search box that adds to it.
 *
 * Replaces two react-select-search widgets that paged their options in
 * on scroll and kept three parallel arrays each in sync by hand.
 */
const AudiencePicker = ({ label, hint, selected, onAdd, onRemove }) => {
  const [term, setTerm] = useState("");
  const [runSearch, { data, loading }] = useLazyQuery(SEARCH_USERS, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      if (term.trim()) runSearch({ variables: { fullName: term.trim() } });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [term, runSearch]);

  const results = ((data && data.getUsersByAdmin) || []).filter(
    (user) => !selected.some((chosen) => chosen._id === user._id)
  );

  return (
    <div className="tripPromotionAudience">
      <span className="dsFieldLabel">{label}</span>
      {hint && <span className="dsFieldHint">{hint}</span>}

      {selected.length > 0 && (
        <div className="tripPromotionChips">
          {selected.map((user) => (
            <span className="tripPromotionChip" key={user._id}>
              {user.fullName || user._id}
              {user.phoneNumber && <span className="tripPromotionChipMeta">{user.phoneNumber}</span>}
              <IconButton
                size="small"
                className="tripPromotionChipRemove"
                onClick={() => onRemove(user._id)}
                title="Remove"
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </IconButton>
            </span>
          ))}
        </div>
      )}

      <SearchField
        placeholder="Search customers by name..."
        value={term}
        onChange={(e) => setTerm(e.target.value)}
      />

      {term.trim() !== "" && (
        <div className="tripPromotionResults">
          {loading && <div className="tripPromotionResultEmpty">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="tripPromotionResultEmpty">No customers match that name.</div>
          )}
          {!loading &&
            results.map((user) => (
              <button
                type="button"
                className="tripPromotionResult"
                key={user._id}
                onClick={() => {
                  onAdd(user);
                  setTerm("");
                }}
              >
                <span>{user.fullName}</span>
                <span className="tripPromotionChipMeta">{user.phoneNumber}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

/**
 * TripPromotionForm - create or edit a ride/delivery promotion code.
 *
 * Split out of commonComponents/addPromotion/AddPromotionForm, which
 * carried four call sites and four submit branches behind a `model`
 * prop. Two of those branches (`model === "shopAdmin"`) were dead — no
 * call site passes that value — and the shop admin panel has had its own
 * form since its redesign. This file covers the trip-promotion case
 * only, which is the one the Ride and delivery group needs.
 */
const TripPromotionForm = ({ promotion }) => {
  const editing = Boolean(promotion);

  const [promotionCode, setPromotionCode] = useState(
    promotion ? promotion.promotionCode || "" : ""
  );
  const [type, setType] = useState(promotion ? promotion.type || "PERCENT" : "PERCENT");
  const [condition, setCondition] = useState(promotion ? promotion.condition || "TIMELY" : "TIMELY");
  const [appliesTo, setAppliesTo] = useState(promotion ? promotion.for || "ALL" : "ALL");
  const [percent, setPercent] = useState(
    promotion && promotion.percent !== null && promotion.percent !== undefined
      ? String(promotion.percent)
      : ""
  );
  const [maximumPromotion, setMaximumPromotion] = useState(
    promotion && promotion.maximumPromotion !== null && promotion.maximumPromotion !== undefined
      ? String(promotion.maximumPromotion)
      : ""
  );
  const [useLimitCount, setUseLimitCount] = useState(
    promotion && promotion.useLimitCount ? String(promotion.useLimitCount) : ""
  );
  const [from, setFrom] = useState(promotion ? toInput(promotion.from) : "");
  const [to, setTo] = useState(promotion ? toInput(promotion.to) : "");

  const initialAudience =
    promotion && promotion.canUse && promotion.canUse.length
      ? "only"
      : promotion && promotion.canNotUse && promotion.canNotUse.length
      ? "everyoneExcept"
      : "everyone";
  const [audience, setAudience] = useState(initialAudience);
  const [included, setIncluded] = useState([]);
  const [excluded, setExcluded] = useState([]);

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  const audienceIds = useMemo(() => {
    const ids = [];
    if (promotion && promotion.canUse) ids.push(...promotion.canUse);
    if (promotion && promotion.canNotUse) ids.push(...promotion.canNotUse);
    // Only well-formed ids reach the document, since they are inlined
    // into it rather than passed as variables.
    return ids
      .filter((id) => typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id))
      .slice(0, AUDIENCE_LOOKUP_LIMIT);
  }, [promotion]);

  const audienceQuery = useMemo(
    () => (audienceIds.length ? buildAudienceQuery(audienceIds) : null),
    [audienceIds]
  );

  // useQuery needs a document even when skipped, so a stable placeholder
  // stands in rather than rebuilding one on every render.
  const { data: audienceData } = useQuery(audienceQuery || AUDIENCE_PLACEHOLDER_QUERY, {
    skip: !audienceQuery,
    errorPolicy: "all",
  });

  // Resolve the stored ids into names once they come back. If the lookup
  // fails the ids are still shown, so an edit never silently drops the
  // audience it was carrying.
  useEffect(() => {
    if (!promotion) return;
    const found = [];
    if (audienceData) {
      Object.keys(audienceData).forEach((key) => {
        const rows = audienceData[key];
        if (Array.isArray(rows)) found.push(...rows.filter(Boolean));
      });
    }
    const resolve = (ids) =>
      (ids || []).map((id) => found.find((user) => user && user._id === id) || { _id: id });
    setIncluded(resolve(promotion.canUse));
    setExcluded(resolve(promotion.canNotUse));
  }, [promotion, audienceData]);

  const [createPromotion] = useMutation(CREATE_PROMOTION);
  const [updatePromotion] = useMutation(UPDATE_PROMOTION);

  /* ---------------- unsaved changes ---------------- */

  const dirty =
    !saved &&
    (editing
      ? promotionCode !== (promotion.promotionCode || "") ||
        type !== promotion.type ||
        condition !== promotion.condition ||
        appliesTo !== (promotion.for || "ALL") ||
        percent !== String(promotion.percent === null ? "" : promotion.percent) ||
        maximumPromotion !==
          String(promotion.maximumPromotion === null ? "" : promotion.maximumPromotion) ||
        useLimitCount !== String(promotion.useLimitCount || "") ||
        from !== toInput(promotion.from) ||
        to !== toInput(promotion.to) ||
        audience !== initialAudience
      : Boolean(promotionCode || percent || maximumPromotion || useLimitCount || from || to));

  useEffect(() => {
    window.onbeforeunload = dirty ? () => true : undefined;
    return () => {
      window.onbeforeunload = undefined;
    };
  }, [dirty]);

  /* ---------------- validation ---------------- */

  const validate = () => {
    const next = {};

    if (!promotionCode.trim()) next.promotionCode = "Give the promotion a code";

    if (!useLimitCount) next.useLimitCount = "Set how many times each customer can use it";
    else if (!/^\d+$/.test(useLimitCount) || parseInt(useLimitCount, 10) < 1) {
      next.useLimitCount = "Must be a whole number of 1 or more";
    }

    if (type === "PERCENT") {
      if (!percent) next.percent = "Set the discount percentage";
      else if (Number(percent) <= 0 || Number(percent) > 100) {
        next.percent = "Must be between 1 and 100";
      }
    } else if (!percent) {
      next.percent = "Set the discount amount";
    }

    if (!maximumPromotion) next.maximumPromotion = "Set the most this code can take off";
    else if (Number(maximumPromotion) <= 0) next.maximumPromotion = "Must be more than zero";

    if (!from) next.from = "Set when the code starts";
    // A new code cannot start in the past; an existing one keeps whatever
    // start date it already has, which may well be behind us.
    else if (!editing && moment(from).isBefore(moment())) {
      next.from = "Start date must be in the future";
    }

    if (!to) next.to = "Set when the code expires";
    else if (from && !moment(to).isAfter(moment(from))) {
      next.to = "Expiry must be after the start date";
    }

    if (audience === "only" && included.length === 0) {
      next.audience = "Choose at least one customer, or open it up to everyone";
    }
    if (audience === "everyoneExcept" && excluded.length === 0) {
      next.audience = "Choose at least one customer to exclude";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /* ---------------- submit ---------------- */

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (submitting) return;
    setFormError("");
    if (!validate()) return;

    setSubmitting(true);
    const variables = {
      promotionCode: promotionCode.trim(),
      type,
      condition,
      for: appliesTo,
      percent: parseFloat(percent),
      maximumPromotion: parseFloat(maximumPromotion),
      useLimitCount: parseInt(useLimitCount, 10),
      from: moment(from).utc(),
      to: moment(to).utc(),
      // Sent as empty arrays rather than undefined when editing, so
      // clearing an audience actually clears it on the server.
      canUse: audience === "only" ? included.map((u) => u._id) : [],
      canNotUse: audience === "everyoneExcept" ? excluded.map((u) => u._id) : [],
    };

    const run = editing
      ? updatePromotion({ variables: { ...variables, promotionId: promotion._id } })
      : createPromotion({ variables });

    run
      .then(() => {
        setSaved(true);
        window.onbeforeunload = undefined;
        window.location.href = `${endPoint}/superAdmin/tripsPromotions`;
      })
      .catch((err) => {
        setSubmitting(false);
        setFormError(readableError(err));
      });
  };

  const onCancel = () => {
    window.onbeforeunload = undefined;
    setSaved(true);
    window.location.href = `${endPoint}/superAdmin/tripsPromotions`;
  };

  /* ---------------- render ---------------- */

  const previewValue =
    type === "PERCENT"
      ? `${percent || 0}% off`
      : `$${Number(percent || 0).toFixed(2)} off`;

  return (
    <div className="page tripPromotionPage">
      <main className="main">
        <PageHeader
          title={editing ? "Edit promotion" : "New trip promotion"}
          description={
            editing
              ? "Changes apply to every customer who has not already redeemed this code."
              : "Create a discount code for rides and deliveries."
          }
          actions={
            <React.Fragment>
              <Button variant="secondary" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" onClick={onSubmit} loading={submitting}>
                {editing ? "Save changes" : "Create promotion"}
              </Button>
            </React.Fragment>
          }
        />

        <Prompt when={dirty} message="You have unsaved changes. Leave this page?" />

        {formError && (
          <div className="dsFormMessage error" role="alert">
            {formError}
          </div>
        )}

        <form className="tripPromotionLayout" onSubmit={onSubmit}>
          <Panel title="The code" subtitle="What customers type in at checkout.">
            <div className="tripPromotionGrid">
              <Field label="Promotion code" htmlFor="promotionCode" required error={errors.promotionCode}>
                <input
                  id="promotionCode"
                  className="dsInput"
                  value={promotionCode}
                  onChange={(e) => setPromotionCode(e.target.value.toUpperCase())}
                  placeholder="SUMMER25"
                  autoFocus
                />
              </Field>

              <Field label="Applies to" htmlFor="appliesTo">
                <FilterDropdown
                  label={FOR_LABELS[appliesTo]}
                  options={FOR_OPTIONS}
                  value={appliesTo}
                  active={false}
                  onChange={(e) => setAppliesTo(e.target.value)}
                />
              </Field>

              <Field
                label="Condition"
                hint={
                  condition === "FIRST_TRIP"
                    ? "Only redeemable on a customer's very first trip."
                    : "Redeemable any time inside the window below."
                }
              >
                <FilterDropdown
                  label={CONDITION_LABELS[condition]}
                  options={CONDITION_OPTIONS}
                  value={condition}
                  active={false}
                  onChange={(e) => setCondition(e.target.value)}
                />
              </Field>

              <Field label="Discount type">
                <FilterDropdown
                  label={TYPE_LABELS[type] || type}
                  options={
                    // An existing FIXED promotion keeps its type even
                    // though new ones are percentage-only.
                    TYPE_OPTIONS.some((o) => o.value === type)
                      ? TYPE_OPTIONS
                      : [...TYPE_OPTIONS, { value: type, label: TYPE_LABELS[type] || type }]
                  }
                  value={type}
                  active={false}
                  onChange={(e) => setType(e.target.value)}
                />
              </Field>
            </div>
          </Panel>

          <Panel title="What it is worth" subtitle={`Preview: ${previewValue}`}>
            <div className="tripPromotionGrid">
              <Field
                label={type === "PERCENT" ? "Percentage off" : "Amount off"}
                htmlFor="percent"
                required
                error={errors.percent}
                hint={type === "PERCENT" ? "Between 1 and 100." : undefined}
              >
                <input
                  id="percent"
                  className="dsInput"
                  type="number"
                  min="0"
                  step={type === "PERCENT" ? "1" : "0.01"}
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                />
              </Field>

              <Field
                label="Maximum discount"
                htmlFor="maximumPromotion"
                required
                error={errors.maximumPromotion}
                hint="The most this code can ever take off one trip."
              >
                <input
                  id="maximumPromotion"
                  className="dsInput"
                  type="number"
                  min="0"
                  step="0.01"
                  value={maximumPromotion}
                  onChange={(e) => setMaximumPromotion(e.target.value)}
                />
              </Field>

              <Field
                label="Uses per customer"
                htmlFor="useLimitCount"
                required
                error={errors.useLimitCount}
              >
                <input
                  id="useLimitCount"
                  className="dsInput"
                  type="number"
                  min="1"
                  step="1"
                  value={useLimitCount}
                  onChange={(e) => setUseLimitCount(e.target.value)}
                />
              </Field>
            </div>
          </Panel>

          <Panel title="When it runs">
            <div className="tripPromotionGrid">
              <Field label="Starts" htmlFor="from" required error={errors.from}>
                <input
                  id="from"
                  className="dsInput"
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </Field>
              <Field label="Expires" htmlFor="to" required error={errors.to}>
                <input
                  id="to"
                  className="dsInput"
                  type="datetime-local"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </Field>
            </div>
            {from && to && moment(to).isAfter(moment(from)) && (
              <div className="tripPromotionWindow">
                <StatusBadge
                  status={moment(from).isAfter(moment()) ? "info" : "success"}
                  label={moment(from).isAfter(moment()) ? "Scheduled" : "Live once saved"}
                />
                <span>
                  Runs for {moment(to).diff(moment(from), "days")} day
                  {moment(to).diff(moment(from), "days") === 1 ? "" : "s"}
                </span>
              </div>
            )}
          </Panel>

          <Panel title="Who can use it">
            <Field error={errors.audience}>
              <div className="tripPromotionAudienceChoice">
                {AUDIENCE_OPTIONS.map((option) => (
                  <label
                    className={`dsOptionCard ${audience === option.value ? "isSelected" : ""}`}
                    key={option.value}
                  >
                    <input
                      type="radio"
                      name="audience"
                      checked={audience === option.value}
                      onChange={() => setAudience(option.value)}
                    />
                    <span className="dsOptionCardText">
                      <strong>{option.label}</strong>
                    </span>
                  </label>
                ))}
              </div>
            </Field>

            {audience === "only" && (
              <AudiencePicker
                label="Customers who can use it"
                hint="Nobody else will be able to redeem this code."
                selected={included}
                onAdd={(user) => setIncluded((current) => current.concat(user))}
                onRemove={(id) => setIncluded((current) => current.filter((u) => u._id !== id))}
              />
            )}

            {audience === "everyoneExcept" && (
              <AudiencePicker
                label="Customers who cannot use it"
                hint="Everyone else can redeem this code."
                selected={excluded}
                onAdd={(user) => setExcluded((current) => current.concat(user))}
                onRemove={(id) => setExcluded((current) => current.filter((u) => u._id !== id))}
              />
            )}
          </Panel>

          <div className="tripPromotionFooter">
            <span className="tripPromotionSummary">
              <LocalOfferIcon fontSize="small" />
              {promotionCode ? promotionCode : "Your code"} · {previewValue} ·{" "}
              {FOR_LABELS[appliesTo]}
            </span>
            <span className="tripPromotionFooterActions">
              <Button variant="secondary" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={submitting}>
                {editing ? "Save changes" : "Create promotion"}
              </Button>
            </span>
          </div>
        </form>
      </main>
    </div>
  );
};

export default TripPromotionForm;
